import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Lightbox from "@/components/Lightbox";
import ShareButton from "@/components/ShareButton";
import HeartButton from "@/components/HeartButton";
import FavouritesWidget from "@/components/FavouritesWidget";
import { resolveImage } from "@/lib/adminApi";
import useScrollTop from "@/hooks/useScrollTop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dedicated painting page — big canvas, click-to-zoom lightbox with pan &
// zoom, and (when room photos exist) a proper multi-image viewer: click a
// thumbnail to swap the hero, or step through inside the lightbox itself.
export default function PaintingDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from === "grid" ? "grid" : "hall";
  const backPath = from === "grid" ? "/paintings" : "/";
  const backLabel = from === "grid" ? "Back to the catalogue" : "Back to the gallery";
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useScrollTop();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/paintings/by-slug/${slug}`);
        if (mounted) { setP(data); setActive(0); }
      } catch (e) {
        if (mounted) setErr(e.response?.status === 404 ? "Painting not found." : "Failed to load painting.");
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  // Merge main image + room photos into a single navigable set
  const images = useMemo(() => {
    if (!p) return [];
    const main = { src: resolveImage(p.image_url), alt: p.alt, label: "Canvas" };
    const rooms = (p.room_photos || []).slice(0, 5).map((u, i) => ({
      src: resolveImage(u),
      alt: `${p.title} in a room ${i + 1}`,
      label: `Room ${i + 1}`,
    }));
    return [main, ...rooms];
  }, [p]);

  // Keyboard prev/next on the page — disabled while the lightbox is open
  // (the Lightbox has its own handler, avoiding a double-advance).
  useEffect(() => {
    if (!images.length || lightbox) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft")  setActive((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length, lightbox]);

  if (err) {
    return (
      <div className="detail-root">
        <p className="detail-empty">{err}</p>
        <Link to={backPath} className="detail-back mono-caps">&larr; {backLabel}</Link>
      </div>
    );
  }
  if (!p) return <div className="detail-root"><p className="detail-empty">Loading…</p></div>;

  const hero = images[active] || images[0];
  const multi = images.length > 1;

  const next = () => setActive((i) => (i + 1) % images.length);
  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);

  return (
    <div className="detail-root" data-testid="detail-root">
      <header className="detail-header">
        <button
          className="detail-back mono-caps"
          onClick={() => {
            if (window.history.state && window.history.length > 1) navigate(-1);
            else navigate(backPath);
          }}
          data-testid="detail-back"
        >
          &larr; {backLabel}
        </button>
        <span className="detail-brand mono-caps">Warren Wulkan</span>
      </header>
      <FavouritesWidget />

      <section className="detail-hero">
        {p.status_tag && (
          <div className="detail-numblock">
            <span className="detail-sticker" data-testid="detail-sticker">{p.status_tag}</span>
          </div>
        )}
        <h1 className="detail-title" data-testid="detail-title">{p.title}</h1>
        <p className="detail-spec mono-caps" data-testid="detail-spec">
          <span>{p.medium}</span>
          <span className="detail-spec-sep">/</span>
          <span>{p.dimensions_display || p.dimensions}</span>
        </p>
        {p.price_display && (
          <p className="detail-price mono-caps" data-testid="detail-price">{p.price_display}</p>
        )}

        <div className="detail-viewer">
          {multi && (
            <button
              type="button"
              className="detail-nav detail-nav--prev"
              onClick={prev}
              aria-label="Previous image"
              data-testid="detail-prev"
            >&larr;</button>
          )}

          <figure
            className="detail-frame"
            onClick={() => setLightbox(true)}
            role="button"
            aria-label="Zoom into painting"
            data-testid="detail-frame"
            data-cursor="Zoom"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setLightbox(true)}
          >
            <img src={hero.src} alt={hero.alt} loading="eager" draggable="false" />
            <span className="detail-frame__hint mono-caps">Click to zoom into the texture</span>
          </figure>

          {multi && (
            <button
              type="button"
              className="detail-nav detail-nav--next"
              onClick={next}
              aria-label="Next image"
              data-testid="detail-next"
            >&rarr;</button>
          )}
        </div>

        {multi && (
          <div className="detail-thumbs" data-testid="detail-thumbs">
            {images.map((img, i) => (
              <button
                key={img.src}
                type="button"
                className={`detail-thumb ${i === active ? "is-active" : ""}`}
                onClick={() => setActive(i)}
                aria-label={img.label}
                aria-current={i === active}
                data-testid={`detail-thumb-${i}`}
              >
                <img src={img.src} alt={img.alt} loading="lazy" draggable="false" />
              </button>
            ))}
          </div>
        )}

        <p className="detail-story" data-testid="detail-story">{p.story}</p>

        <div className="detail-footer">
          <div className="detail-footer__actions">
            <HeartButton slug={p.slug} title={p.title} className="heart-btn--detail" testId="heart-detail" withLabel />
            <ShareButton slug={p.slug} title={p.title} />
            <a
              className="detail-contact mono-caps"
              href={`mailto:info@wwabstract.com?subject=${encodeURIComponent("Inquiry — " + p.title)}`}
              data-testid="detail-contact"
            >
              Contact the artist
            </a>
          </div>
        </div>
      </section>

      <Lightbox
        src={hero?.src}
        alt={hero?.alt}
        open={lightbox}
        onClose={() => setLightbox(false)}
        onPrev={multi ? prev : null}
        onNext={multi ? next : null}
        counter={multi ? `${active + 1} / ${images.length}` : null}
      />
    </div>
  );
}
